import { z } from 'zod';
import { RequestedRequestViewSchema } from './requested_request_view';
import { TripViewSchema } from './trip_view';

/**
 * A trip the logged-in REQUESTER booked for someone else, as a board card.
 * Produced by trip-api `GET /internal/requesters/:requesterRef/requested-trips`
 * (traveler + trip fields), stitched by the app-integration BFF
 * (`approvableVoucherIds`, `approvalExpiresAt`) and consumed by the web-chat
 * `/requested` board. The card's `vouchers` are ONLY the ones the requester
 * asked for — services the traveler booked alone never cross this wire.
 * Stage/column is NOT on the wire: the client derives it (`boardStage`).
 */
export const RequestedTripViewSchema = TripViewSchema.extend({
  /** WHO TRAVELS. `id` is the opaque publicId taken from the first resolved
   *  voucher (`voucher.traveler.id`); null when no voucher resolved. */
  traveler: z.object({
    id: z.string().nullable(),
    name: z.string().nullable(),
    /** URL pré-assinada da foto do viajante (#2640), costurada pelo BFF via
     *  `POST /internal/customers/photos` — só quando viajante e solicitante
     *  são da MESMA empresa (quem decide é o customer-api). `null` = sem foto
     *  ou fora da regra; ausente = BFF antigo. */
    photoUrl: z.url().nullable().optional(),
  }),
  /** Vouchers on this card that ALSO await the requester's own approval
   *  (requester ∈ voucher.userApprovals). Stitched by the BFF; default empty. */
  approvableVoucherIds: z.array(z.string()).default([]),
  /** voucherId → fare deadline (ISO datetime WITH offset) for the approvable
   *  vouchers, from the approval triage read. `null` = no deadline recorded.
   *  Stitched by the BFF, which maps anything upstream sends that is not a valid
   *  ISO datetime to `null` — the client feeds these straight into `new Date()`
   *  and a malformed string would silently skip expiry classification. */
  approvalExpiresAt: z.record(z.string(), z.iso.datetime({ offset: true }).nullable()).default({}),
  /** QUEM pediu a viagem. Só vem nos cartões que o solicitante acompanha e que
   *  outra pessoa pediu; `null` = ela mesma pediu, ou BFF antigo. */
  requestedBy: z.object({ name: z.string() }).nullable().default(null),
});
export type RequestedTripView = z.infer<typeof RequestedTripViewSchema>;

export const RequestedTripsResponseSchema = z.object({
  trips: z.array(RequestedTripViewSchema),
  /** Pedidos em atendimento humano (spec 2026-09-12). Default [] — BFF antigo segue válido. */
  requests: z.array(RequestedRequestViewSchema).default([]),
});
export type RequestedTripsResponse = z.infer<typeof RequestedTripsResponseSchema>;
